import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import {
  IntegrationsSiteSettingsSubspaceComponent,
  IntegrationsSubspaceName
} from '~src/app/spaces/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { ConnectedResourceGovernanceSubspace } from '~src/schedule/components/ConnectedResourceGovernanceSubspace';
import { ResourceGovernanceSubspaceName } from '~src/schedule/components/ResourceGovernanceSubspace';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { SiteRoles } from '~src/ts/ServerApi/types/ISiteRole';
import { Sites } from '~src/ts/siteManagement/services/resources/Sites';

import { Messages } from '../../../codegen/Localize';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { SessionServerSelectors, SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { ServerUrls } from '../../ServerUrls';
import { AppState } from '../../state/AppState';
import { ServerSettingsSubspaceName } from '../../state/SpaceInfo';
import { ExtensionsServerSettingsSubspaceComponent } from '../ServerSettingsSpace/ExtensionsServerSettingsSubspace';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { AddSiteSettingsSubspace } from './AddSiteSettingsSubspace';
import { CustomizationSettingsSubspace } from './CustomizationSettingsSubspace';
import { GeneralServerSettingsSubspace } from './GeneralServerSettingsSubspace';
import { LicensesSettingsSubspace } from './LicensesSettingsSubspace';



export interface ServerSettingsSpaceOwnProps {
  readonly subspaceName: ServerSettingsSubspaceName;
}

export interface ServerSettingsServicesProps {
  readonly server: SessionManagementService;
}

export interface ServerSettingsStateProps {
  readonly isSessionMultiSite: boolean;
  readonly isOnlineEnvironment: boolean;
}

export type ServerSettingsNonServicesProps = ServerSettingsSpaceOwnProps & ServerSettingsStateProps;
export type ServerSettingsSpaceProps = ServerSettingsNonServicesProps & ServerSettingsServicesProps;

export class ServerSettingsSpace extends React.PureComponent<ServerSettingsSpaceProps> {

  private static SubSpaceToTitle: {[P in ServerSettingsSubspaceName]: () => string} = {
    settings: Messages.serverSettings_general,
    licenses: Messages.serverSettings_licenses,
    extensions: Messages.serverSettings_extensions,
    addsite: Messages.serverSettings_addSite,
    customization: Messages.serverSettings_customization,
    integrations: Messages.serverSettings_integrations,
    resourcesgovernance: Messages.resourceGovernanceTitle,
  };

  public componentDidMount() {
    const title = ServerSettingsSpace.SubSpaceToTitle[this.props.subspaceName]();
    BrowserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: title});
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.server,
      this.props.isSessionMultiSite,
      this.props.isOnlineEnvironment
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'server-settings'}
        spaceItemName={''}
      />
    );
  }

  private static settingsUrlBySubspace: Record<ServerSettingsSubspaceName, () => string> = {
    settings: () => ServerUrls.getInstance().settingsUrl(),
    extensions: () =>  ServerUrls.getInstance().settingsExtensionsUrl(),
    addsite: () =>  ServerUrls.getInstance().addSiteUrl(),
    licenses: () =>  ServerUrls.getInstance().settingsLicensesUrl(),
    customization: () => ServerUrls.getInstance().settingsCustomizationUrl(),
    integrations: () => ServerUrls.getInstance().settingsIntegrationsUrl(),
    resourcesgovernance: () => ServerUrls.getInstance().settingsResourceLimitsUrl(),
  };

  private getSettingsUrl = (subspaceName?: ServerSettingsSubspaceName) => {
    if (subspaceName) {
      return ServerSettingsSpace.settingsUrlBySubspace[subspaceName]();
    }
    return '';
  }
}

function getSettingsSubspaces(
  server: SessionManagementService,
  isSessionMultiSite: boolean,
  isOnlineEnvironment: boolean
): ReadonlyArray<Subspace> {
  return _.compact([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralServerSettingsSubspace
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          serverSettingsService={ServerSettingsInstance}
          sites={Sites.instance}
        />
      )
    },
    {
      name: 'licenses',
      tabLabel: Messages.licenses(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <LicensesSettingsSubspace
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsServerSettingsSubspaceComponent
          server={server}
          isSessionMultiSite={isSessionMultiSite}
        />
      )
    },
    FeatureFlags.serverWelcomeChannelEnabled && {
      name: 'customization',
      tabLabel: Messages.customizationSetting_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <CustomizationSettingsSubspace />
      )
    },
    isIntegrationsTabEnabledForServerSettings() && {
      name: IntegrationsSubspaceName,
      tabLabel: Messages.integrations_settingsTabTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <IntegrationsSiteSettingsSubspaceComponent
          siteName={undefined}
          urls={undefined}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={true}
          isOnlineEnvironment={isOnlineEnvironment}
          userRole={SiteRoles.ServerAdministrator}
        />
      )
    },
    FeatureFlags.onPremiseGovernanceEnabled && {
      name: ResourceGovernanceSubspaceName,
      tabLabel: Messages.resourceGovernanceTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => <ConnectedResourceGovernanceSubspace />,
    },
    !isSessionMultiSite && {
      name: 'addsite',
      tabLabel: Messages.addASite(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <AddSiteSettingsSubspace/>
      )
    },
  ]);
}

export const isIntegrationsTabEnabledForServerSettings = () => {
  const sessionManagementService = getSessionManagementServiceInstance();
  let singleSiteServer_SlackSettingsEnabled = sessionManagementService.isSingleSiteOnPremServer() && FeatureFlags.userNotificationSlackChannel;
  return FeatureFlags.publishingSalesforceBundlesEnabled || singleSiteServer_SlackSettingsEnabled;
};

export const ConnectedServerSettingsSpace: React.ComponentClass<ServerSettingsSpaceOwnProps & ServerSettingsServicesProps>
  = connect((state: AppState) => ({
    isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
  }))(ServerSettingsSpace);